/*!************************************************************************
 *
 * CAPGEMINI CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 CAPGEMINI NV/SA
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Capgemini NV/SA. The intellectual and
 * technical concepts contained herein are proprietary to
 * Capgemini NV/SA and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Capgemini NV/SA.
 **************************************************************************/
'use strict';

var ResponsiveImage = {
    VERSION: '1.0.8',
    SRC: 'src',
    BREAKPOINTS: 'breakpoints',
    WIDTH: 'width',
    HEIGHT: 'height',
    LOADED_WIDTH: 'loadedWidth',
    RESPONSIVE_CLASS: 'dm-responsive-image',
    STATIC_CLASS: 'dm-static-image',
    INITIALIZED_CLASS: 'dm-responsive-image-initialized',
    IMG_TAG: 'IMG',
    DPR: window.devicePixelRatio > 2 ? window.devicePixelRatio : 2,
    WATCHED_ELEMENTS: {},
    WATCHED_BREAKPOINTS: [],
    MEDIA_CHANGE_LISTENERS: {},
    AVAILABLE_ASSETS: {},
    IS_LOADED: false,
    DEFAULT_BREAKPOINTS: '768,1025',
    SMART_CROP_RATIOS: {
        'Portrait': 2500 / 5000,
        'Square': 5000 / 5000,
        '4-by-3': 5000 / 3750,
        '3-by-2': 5000 / 3333,
        'Widescreen': 5000 / 2813,
        'Super-widescreen': 5000 / 2143,
		'3-by-4': 3750 / 5000
    },
    PRESET_RATIOS: {
        'Square': 1
    },
    init: function init(isLazy) {
        Array.prototype.forEach.call(document.getElementsByClassName(ResponsiveImage.RESPONSIVE_CLASS), function (element) {
            if (!element.classList.contains(ResponsiveImage.INITIALIZED_CLASS) && (!isLazy || isLazy && ResponsiveImage.isElementInViewport(element))) {
                element.classList.add(ResponsiveImage.INITIALIZED_CLASS);

                ResponsiveImage.initializeElement(element);
                ResponsiveImage.watchElement(element);
            }
        });

        Array.prototype.forEach.call(document.getElementsByClassName(ResponsiveImage.STATIC_CLASS), function (element) {
            if (!element.classList.contains(ResponsiveImage.INITIALIZED_CLASS) && (!isLazy || isLazy && ResponsiveImage.isElementInViewport(element))) {
                element.classList.add(ResponsiveImage.INITIALIZED_CLASS);

                ResponsiveImage.initializeStaticElement(element);
            }
        });

        ResponsiveImage.WATCHED_BREAKPOINTS.forEach(function (breakpoint) {
            if (!ResponsiveImage.MEDIA_CHANGE_LISTENERS[breakpoint]) {
                ResponsiveImage.MEDIA_CHANGE_LISTENERS[breakpoint] = window.matchMedia('(min-width: ' + breakpoint + 'px)').onchange = function () {
                    ResponsiveImage.WATCHED_ELEMENTS[breakpoint].forEach(function (element) {
                        ResponsiveImage.initializeElement(element);
                    });
                };
            }
        });
    },
    watchElement: function watchElement(element) {
        var data = element.dataset;
        var breakpoints = (undefined !== data[ResponsiveImage.BREAKPOINTS] ? data[ResponsiveImage.BREAKPOINTS] : ResponsiveImage.DEFAULT_BREAKPOINTS).split(',');
        if (breakpoints) {
            breakpoints.forEach(function (breakpoint) {
                var parsedBreakpoint = parseInt(breakpoint);

                if (ResponsiveImage.WATCHED_BREAKPOINTS.indexOf(parsedBreakpoint) === -1) {
                    ResponsiveImage.WATCHED_BREAKPOINTS.push(parsedBreakpoint);
                }

                if (ResponsiveImage.WATCHED_ELEMENTS[breakpoint]) {
                    ResponsiveImage.WATCHED_ELEMENTS[breakpoint].push(element);
                } else {
                    ResponsiveImage.WATCHED_ELEMENTS[breakpoint] = [element];
                }
            });
        }
    },
    isElementInViewport: function isElementInViewport(element) {
        //Load all images within 1.5 screen heights
        var rect = element.getBoundingClientRect();

        // Updated the left and right checks because of issues with Banner L.
        // With this fix image that are wider then the viewport will also trigger
        // a true when in view.
        //
        // Ticket: https://rituals.atlassian.net/browse/DL-27918
        // Original line: return rect.top >= 0 && rect.left >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight) * 1.5 && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
        return rect.top >= 0 &&
            rect.top <= (window.innerHeight || document.documentElement.clientHeight) * 1.5 &&
            (
                (rect.left >= 0 && rect.right <= (window.innerWidth || document.documentElement.clientWidth)) ||
                (rect.left < 0 && rect.right > (window.innerWidth || document.documentElement.clientWidth))
            );
    },
    initializeStaticElement: function initializeStaticElement(element) {
        var data = element.dataset;
        var transformations = ResponsiveImage.getAssetTransformations(data);
        var settings = {
            asset: data[ResponsiveImage.SRC],
            width: data[ResponsiveImage.WIDTH] || transformations.wid,
            height: data[ResponsiveImage.HEIGHT] || transformations.hei,
            transformations: transformations
        };

        if (settings.transformations.smartCrop) {
            ResponsiveImage.initializeElementWithSmartCrop(element, settings, data, true);
        } else {
            ResponsiveImage.setStaticSource(element, settings);
        }
    },
    initializeElement: function initializeElement(element, forceInitialization) {
        var data = element.dataset;
        var settings = {
            asset: data[ResponsiveImage.SRC],
            breakpoints: data[ResponsiveImage.BREAKPOINTS] && data[ResponsiveImage.BREAKPOINTS].split(',').sort(function (a, b) {
                return parseInt(a) - parseInt(b);
            }),
            transformations: ResponsiveImage.getAssetTransformations(data),
            width: ResponsiveImage.getWidthForElement(element)
        };

        if (forceInitialization || !data[ResponsiveImage.LOADED_WIDTH] || parseInt(data[ResponsiveImage.LOADED_WIDTH]) < settings.width) {
            if (settings.transformations.smartCrop) {
                ResponsiveImage.initializeElementWithSmartCrop(element, settings, data, false);
            } else if (settings.transformations.preset) {
                ResponsiveImage.initializeElementWithPreset(element, settings);
            } else {
                ResponsiveImage.setSource(element, settings);
            }
        }
    },
    initializeElementWithSmartCrop: function initializeElementWithSmartCrop(element, settings, data, isStaticElement) {
        settings.ratio = ResponsiveImage.SMART_CROP_RATIOS[settings.transformations.smartCrop];
        isStaticElement ? ResponsiveImage.setStaticSource(element, settings) : ResponsiveImage.setSource(element, settings);
    },
    initializeElementWithPreset: function initializeElementWithPreset(element, settings) {
        settings.ratio = ResponsiveImage.PRESET_RATIOS[settings.transformations.preset];
        ResponsiveImage.setSource(element, settings);
    },
    getAssetTransformations: function getAssetTransformations(data) {
        var queryParameters = ResponsiveImage.getQueryParameters(data[ResponsiveImage.SRC] || '');
        queryParameters.smartCrop = ResponsiveImage.getSmartCrop(data);

        return queryParameters;
    },
    getQueryParameters: function getQueryParameters(src) {
        var queryParameters = {};
        var splitSrc = src.split('?');
        var queryString = splitSrc[1];
        if (queryString) {
            var parameters = queryString.split('&');
            for (var i = 0; i < parameters.length; i++) {
                var parameter = parameters[i];
                var splitParameter = parameter.split('=');
                if (splitParameter[0] && splitParameter[1]) {
                    queryParameters[splitParameter[0]] = splitParameter[1];
                } else if (splitParameter[0].indexOf('$') === 0) {
                    queryParameters.preset = splitParameter[0];
                }
            }
        }

        return queryParameters;
    },
    getSmartCrop: function getSmartCrop(data) {
        var src = data[ResponsiveImage.SRC] || '';
        var splitSrc = src.split(':');
        var isWithoutSmartCropInUrl = src.lastIndexOf(':') === 5 || src.lastIndexOf(':') === -1;
        return isWithoutSmartCropInUrl ? '' : splitSrc[splitSrc.length - 1].split('?')[0];
    },
    setStaticSource: function setStaticSource(element, settings) {
        var queryStringPrefix = settings.asset.indexOf('?') === -1 ? '?' : '&';
        var width = settings.width || settings.ratio && settings.height && settings.height * settings.ratio;
        var height = settings.height || settings.ratio && settings.width && settings.width / settings.ratio;
        var imageSize = (width || height)
            ? queryStringPrefix + ((width ? 'wid=' + Math.round(width) : '') +
            (height ? (width ? '&' : '') + 'hei=' + Math.round(height) : ''))
            : '';
        if (element.tagName === ResponsiveImage.IMG_TAG) {
            //Set image source
            element.src = settings.asset + imageSize;
        } else {
            //Set background image
            element.style.backgroundImage = 'url(' + settings.asset + imageSize + ')';
        }
        element.classList.add(ResponsiveImage.INITIALIZED_CLASS);
    },
    setSource: function setSource(element, settings) {
        var data = element.dataset;
        var highestLoadedWidth = ResponsiveImage.AVAILABLE_ASSETS[settings.asset];
        settings.width = ResponsiveImage.getClosestBreakpoint(settings.breakpoints, settings.width) || settings.width;
        data[ResponsiveImage.LOADED_WIDTH] = settings.width;

        if (highestLoadedWidth > settings.width) {
            settings.width = highestLoadedWidth;
        } else {
            settings.requestNewImage = true;
        }

        settings.url = settings.asset + ((settings.asset && settings.asset.indexOf('?') === -1) ? '?' : '&') + 'wid=' + Math.round(settings.width * ResponsiveImage.DPR) + (settings.ratio ? '&hei=' + Math.round(settings.width * ResponsiveImage.DPR / settings.ratio) : '');

        if (element.tagName === ResponsiveImage.IMG_TAG) {
            //Set image source
            ResponsiveImage.setImageSource(element, settings);
        } else {
            //Set background image
            ResponsiveImage.setBackgroundImage(element, settings);
        }
    },
    setImageSource: function setImageSource(element, settings) {
        if (settings.requestNewImage) {
            element.onload = function () {
                ResponsiveImage.AVAILABLE_ASSETS[settings.asset] = settings.width;
            };
        }

        element.src = settings.url;
        element.classList.add(ResponsiveImage.INITIALIZED_CLASS);
    },
    setBackgroundImage: function setBackgroundImage(element, settings) {
        if (settings.requestNewImage) {
            var image = new Image();

            image.onload = function () {
                element.style.backgroundImage = 'url(' + settings.url + ')';
                ResponsiveImage.AVAILABLE_ASSETS[settings.asset] = parseInt(settings.width);
            };

            image.src = settings.url;
        } else {
            element.style.backgroundImage = 'url(' + settings.url + ')';
        }
        element.classList.add(ResponsiveImage.INITIALIZED_CLASS);
    },
    getWidthForElement: function getWidthForElement(element) {
        var width = element.clientWidth;
        if (width > 1) return width;
        width = element.parentElement && ResponsiveImage.getWidthForElement(element.parentElement);
        return width > 1 ? width : 100;
    },
    getClosestBreakpoint: function getClosestBreakpoint(breakpoints, width) {
        return breakpoints && breakpoints.find(function (breakpoint) {
            return breakpoint > width;
        });
    },
    getAssetSource: function getAssetSource(asset, preset) {
        var src = asset ? asset.split("?")[0] : '';
        var splitSrc = src.split(':');
        var isWithoutSmartCropInUrl = src.lastIndexOf(':') === 5 || src.lastIndexOf(':') === -1;
        var startsWithHttps = src.indexOf('https') === 0;
        return (isWithoutSmartCropInUrl ? src : startsWithHttps ? [splitSrc[0], ':', splitSrc[1]].join('') : splitSrc[0]) + (preset ? ('?' + preset) : '');
    },
    fetchSmartCrops: function fetchSmartCrops(url, callback) {
        var handlerName = 'handler_' + Math.floor(Math.random() * 999999) + 1;
        var script = document.createElement('script');
        script.src = url + '&handler=' + handlerName;
        document.getElementsByTagName('head')[0].appendChild(script);

        window[handlerName] = function (data) {
            delete window[handlerName];
            script.parentElement.removeChild(script);
            callback(data);
        };
    },
    fetchImageProps: function fetchImageProps(url, callback) {
        var handlerName = 'handler_' + Math.floor(Math.random() * 999999) + 1;
        var script = document.createElement('script');
        script.src = url + '&handler=' + handlerName;
        document.getElementsByTagName('head')[0].appendChild(script);

        window[handlerName] = function (data) {
            delete window[handlerName];
            script.parentElement.removeChild(script);
            callback(data);
        };
    }
};
window.addEventListener('dm.image.init', function (e) {
    if (e.detail.element.classList.contains(ResponsiveImage.STATIC_CLASS)) {
        ResponsiveImage.initializeStaticElement(e.detail.element);
    } else {
        ResponsiveImage.initializeElement(e.detail.element, true);
    }
});
window.addEventListener('scroll', function () {
    if (!ResponsiveImage.IS_LOADED) {
        ResponsiveImage.IS_LOADED = true;
        ResponsiveImage.init(false);
    }
});
window.addEventListener('DOMContentLoaded', function () {
    ResponsiveImage.init(true);
});
